import { cn } from '@/utils/cn'
import { useQuery } from '@tanstack/react-query'
import {
  ChevronDown,
  Menu,
  MessageCircle,
  Scale,
  X,
  Instagram,
  Facebook,
  MapPin,
} from 'lucide-react'
import React, { lazy, Suspense } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { supabase } from '@/lib/supabase'
import { CACHE_TIMES } from '@/lib/queryClient'
// Lazy-load the chatbot widget — it's optional UX, ~500KB+ of bundle
// (chatbot/useChatbot.ts is 3000+ lines). Defers loading until idle.
const ChatbotWidget = lazy(() =>
  import('./chatbot').then((m) => ({ default: m.ChatbotWidget }))
)
import { activityPath, useNavActivities } from '@/hooks/useNavActivities'
import CartIcon from './cart/CartIcon'
import CartDrawer from './cart/CartDrawer'
import PreferencesMenu from './PreferencesMenu'
import ThemeToggle from './ThemeToggle'
import LanguageSwitcher from './LanguageSwitcher'

/** Renders a legal sub-section: a bold heading + a disc bullet list, fed from a
 *  translation array (`t(key, { returnObjects: true })`). */
function LegalList({ title, items }: { title: string; items: unknown }) {
  const list = Array.isArray(items) ? (items as string[]) : []
  return (
    <div>
      <h4 className="font-semibold mb-2">{title}</h4>
      <ul className="list-disc pl-6 space-y-1">
        {list.map((it, i) => (
          <li key={i}>{it}</li>
        ))}
      </ul>
    </div>
  )
}

const Layout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isMenuOpen, setIsMenuOpen] = React.useState(false)
  const [showLegalModal, setShowLegalModal] = React.useState(false)
  // The activities list folds and unfolds on click rather than on hover, so it
  // stays put while you read it (and works on a touch screen, where hover only
  // happens as a side effect of tapping).
  const [openMenu, setOpenMenu] = React.useState<string | null>(null)
  // Spans the whole header, so the mobile chevron isn't read as an outside
  // click (which would close the fold a beat before its own handler reopens it).
  const navRef = React.useRef<HTMLElement>(null)
  const location = useLocation()

  React.useEffect(() => {
    if (!openMenu) return
    const onPointerDown = (e: MouseEvent | TouchEvent) => {
      if (!navRef.current?.contains(e.target as Node)) setOpenMenu(null)
    }
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpenMenu(null)
    }
    document.addEventListener('mousedown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('mousedown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [openMenu])

  // Check if we're on the home page, tours page, tour detail pages, activity detail pages, about page, faq page, contact page, blog pages, or self-guided-routes page (to hide footer content)
  const isHomePage = location.pathname === '/'
  const isToursPage = location.pathname === '/tours'
  const isTourDetailPage = location.pathname.startsWith('/tours/')
  const isActivityDetailPage = location.pathname.startsWith('/activities/')
  const isAboutPage = location.pathname === '/about'
  const isFaqPage = location.pathname === '/faq'
  const isContactPage = location.pathname === '/contact'
  const isBlogPage =
    location.pathname === '/blog' || location.pathname.startsWith('/blog/')
  const isSelfGuidedRoutesPage = location.pathname === '/self-guided-routes'
  const isPartnersPage = location.pathname === '/partners'
  const isTreksPage = location.pathname === '/trek'
  const isTrekDetailPage = location.pathname.startsWith('/trek/')
  const isRockClimbingPage = location.pathname === '/rock-climbing'
  const isRockClimbingDetailPage =
    location.pathname.startsWith('/rock-climbing/')

  // Pages whose first paint is a full-bleed hero / gradient wallpaper —
  // the fixed header floats OVER them with no top spacer.
  const hasFloatingHero =
    isHomePage ||
    isToursPage ||
    isTourDetailPage ||
    isActivityDetailPage ||
    isPartnersPage ||
    isTreksPage ||
    isTrekDetailPage ||
    isRockClimbingPage ||
    isRockClimbingDetailPage ||
    isAboutPage ||
    isFaqPage ||
    isContactPage ||
    isBlogPage ||
    isSelfGuidedRoutesPage
  const shouldHideFooterContent =
    isHomePage ||
    isToursPage ||
    isTourDetailPage ||
    isActivityDetailPage ||
    isAboutPage ||
    isFaqPage ||
    isContactPage ||
    isPartnersPage ||
    isBlogPage ||
    isSelfGuidedRoutesPage ||
    isTreksPage ||
    isTrekDetailPage ||
    isRockClimbingPage ||
    isRockClimbingDetailPage

  const { t } = useTranslation('nav')
  const { t: tl, i18n } = useTranslation('legal')
  // The activities entry drops down to every activity we sell. The DB rows come
  // from the same source as the home activities grid (rock climbing collapsed,
  // trekking excluded), so the menu can't drift from the page.
  const { data: navActivities } = useNavActivities()
  const activityLinks = [
    { name: t('allActivities', 'All Activities'), href: '/#other-adventures' },
    // The enduro tours are the flagship product; the menu says what they are
    // ('Enduro'), not what the route is called ('Tours').
    { name: t('enduro', 'Enduro Tour'), href: '/tours' },
    { name: t('treks'), href: '/trek' },
    ...(navActivities ?? [])
      // Menu rows use a short label ("Hot Air Balloon"), not the SEO title
      // ("Vang Vieng Hot Air Balloon Ride") — a product added later with no
      // short key falls back to its full title rather than disappearing.
      .map((a) => ({
        name: t(`activityShort.${a.slug}`, a.title),
        href: activityPath(a),
      }))
      // /tours is already listed above; don't repeat it for the enduro row.
      .filter((l) => l.href !== '/tours'),
  ]
  const navigation = [
    { name: t('home'), href: '/' },
    {
      name: t('activities'),
      href: '/#other-adventures',
      children: activityLinks,
    },
    { name: t('about'), href: '/about' },
    { name: t('blog'), href: '/blog' },
    { name: t('contact'), href: '/contact' },
  ]

  // Fetch chatbot settings - don't show chatbot until we know it's enabled
  const { data: chatbotSettings, isLoading: isChatbotLoading } = useQuery({
    queryKey: ['site-settings', 'chatbot_settings'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('site_settings')
        .select('value')
        .eq('key', 'chatbot_settings')
        .single()

      if (error) return { enabled: true } // Default to enabled if error
      return (data?.value as { enabled: boolean }) || { enabled: true }
    },
    ...CACHE_TIMES.SEMI_STATIC,
  })

  // Hide chatbot while loading to prevent flash, then show based on setting
  const isChatbotEnabled =
    !isChatbotLoading && (chatbotSettings?.enabled ?? true)

  // Any navigation folds it back up.
  React.useEffect(() => {
    setOpenMenu(null)
  }, [location.pathname, location.hash])

  const isActive = (href: string) => {
    if (href === '/') return location.pathname === '/'
    return location.pathname.startsWith(href)
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-garage-surface via-garage-mud to-garage-dark transition-colors">
      {/* Header with Glassmorphism */}
      <header ref={navRef} className="fixed top-0 left-0 right-0 z-50">
        {/* Glassmorphism backdrop */}
        <div className="absolute inset-0 border-b border-garage-cream/10 bg-garage-dark/30 shadow-lg shadow-black/5 backdrop-blur-xl" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="flex justify-between items-center h-12 md:h-14">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2.5 group">
              <img
                src="/logo-white.png"
                alt="Terra Lao Adventure"
                className="h-8 w-8 md:h-10 md:w-10 rounded-full ring-1 ring-garage-accent/25 transition-all duration-300 group-hover:ring-garage-accent/60"
              />
              <span className="hidden md:block text-lg font-bold tracking-tight text-garage-cream font-garage-title">
                Terra Lao <span className="text-garage-accent">Adventure</span>
              </span>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center gap-0.5">
              {navigation.map((item) => {
                const active =
                  isActive(item.href) ||
                  (item.children?.some((c) => isActive(c.href)) ?? false)
                const link = (
                  <Link
                    to={item.href}
                    className={cn(
                      'relative flex items-center gap-1 rounded-lg px-3 py-1.5 text-sm font-medium transition-colors duration-300',
                      active
                        ? 'text-garage-accent'
                        : 'text-garage-cream/70 hover:bg-garage-card/40 hover:text-garage-cream'
                    )}
                  >
                    {item.name}
                    {active && (
                      <span className="absolute inset-x-3 bottom-0.5 h-0.5 rounded-full bg-garage-accent" />
                    )}
                  </Link>
                )

                if (!item.children)
                  return <React.Fragment key={item.name}>{link}</React.Fragment>

                const isOpen = openMenu === item.name
                return (
                  <div key={item.name} className="relative">
                    <button
                      type="button"
                      onClick={() => setOpenMenu(isOpen ? null : item.name)}
                      aria-expanded={isOpen}
                      aria-haspopup="true"
                      className={cn(
                        'relative flex items-center gap-1 rounded-lg px-3 py-1.5 text-sm font-medium transition-colors duration-300',
                        active || isOpen
                          ? 'text-garage-accent'
                          : 'text-garage-cream/70 hover:bg-garage-card/40 hover:text-garage-cream'
                      )}
                    >
                      {item.name}
                      <ChevronDown
                        className={cn(
                          'h-3.5 w-3.5 transition-transform duration-200',
                          isOpen && 'rotate-180'
                        )}
                      />
                      {active && (
                        <span className="absolute inset-x-3 bottom-0.5 h-0.5 rounded-full bg-garage-accent" />
                      )}
                    </button>
                    <div
                      className={cn(
                        'absolute left-0 top-full pt-2',
                        isOpen ? 'block' : 'hidden'
                      )}
                    >
                      <div className="max-h-[70vh] min-w-52 overflow-y-auto whitespace-nowrap rounded-lg border border-garage-cream/10 bg-garage-dark/95 p-1 shadow-xl backdrop-blur-xl">
                        {item.children.map((child) => (
                          <Link
                            key={child.href}
                            to={child.href}
                            className={cn(
                              'block rounded-md px-3 py-2 text-sm font-medium transition-colors',
                              isActive(child.href)
                                ? 'bg-garage-accent/15 text-garage-accent'
                                : 'text-garage-cream/80 hover:bg-garage-card/50 hover:text-garage-cream'
                            )}
                          >
                            {child.name}
                          </Link>
                        ))}
                      </div>
                    </div>
                  </div>
                )
              })}
              <span className="mx-1.5 h-5 w-px bg-garage-cream/10" />
              {/* Language + theme behind one low-profile globe */}
              <PreferencesMenu />
              <CartIcon />
            </nav>

            {/* Mobile controls */}
            <div className="md:hidden flex items-center gap-0.5">
              <CartIcon />
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                aria-label="Menu"
                className="grid h-9 w-9 place-items-center rounded-lg text-garage-cream/80 transition-colors hover:bg-garage-card/40 hover:text-garage-cream"
              >
                {isMenuOpen ? (
                  <X className="h-5 w-5" />
                ) : (
                  <Menu className="h-5 w-5" />
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Navigation with glassmorphism */}
        {isMenuOpen && (
          <div className="md:hidden relative">
            {/* Background blur for mobile menu */}
            <div className="absolute inset-0 bg-garage-dark/30 backdrop-blur-xl border-t border-garage-accent/20" />
            <div className="px-4 py-4 space-y-2 relative">
              {navigation.map((item) => (
                <React.Fragment key={item.name}>
                  <div className="flex items-center gap-1">
                    <Link
                      to={item.href}
                      onClick={() => setIsMenuOpen(false)}
                      className={cn(
                        'flex-1 block px-4 py-3 text-base font-medium transition-all duration-300 rounded-lg',
                        isActive(item.href)
                          ? 'text-garage-accent bg-garage-accent/20 backdrop-blur-sm border border-garage-accent/30'
                          : 'text-garage-cream/80 hover:text-garage-cream hover:bg-garage-card/40 backdrop-blur-sm border border-transparent hover:border-garage-accent/20'
                      )}
                    >
                      {item.name}
                    </Link>
                    {item.children && (
                      // Separate target: tapping the label still navigates,
                      // tapping the chevron only folds the list.
                      <button
                        type="button"
                        onClick={() =>
                          setOpenMenu(
                            openMenu === item.name ? null : item.name
                          )
                        }
                        aria-expanded={openMenu === item.name}
                        aria-label={item.name}
                        className="grid h-11 w-11 shrink-0 place-items-center rounded-lg text-garage-cream/70 transition-colors hover:bg-garage-card/40 hover:text-garage-cream"
                      >
                        <ChevronDown
                          className={cn(
                            'h-5 w-5 transition-transform duration-200',
                            openMenu === item.name && 'rotate-180'
                          )}
                        />
                      </button>
                    )}
                  </div>
                  {item.children && openMenu === item.name && (
                    <div className="ml-4 space-y-1 border-l border-garage-accent/20 pl-3">
                      {item.children.slice(1).map((child) => (
                        <Link
                          key={child.href}
                          to={child.href}
                          onClick={() => setIsMenuOpen(false)}
                          className={cn(
                            'block rounded-md px-3 py-2 text-sm font-medium transition-colors',
                            isActive(child.href)
                              ? 'text-garage-accent'
                              : 'text-garage-cream/70 hover:bg-garage-card/40 hover:text-garage-cream'
                          )}
                        >
                          {child.name}
                        </Link>
                      ))}
                    </div>
                  )}
                </React.Fragment>
              ))}

              {/* Language + theme — one tiny, discreet row */}
              <div className="flex items-center justify-between gap-2 pt-3 mt-3 border-t border-garage-accent/15">
                <LanguageSwitcher variant="compact" />
                <ThemeToggle variant="bare" />
              </div>

              {/* Social Media Links in Mobile Menu */}
              <div className="pt-4 mt-4 border-t border-garage-accent/20">
                <div className="flex justify-center gap-3">
                  <a
                    href="https://www.instagram.com/terra_lao_adventure/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group relative"
                  >
                    <div className="absolute inset-0 bg-gradient-to-br from-purple-500/20 to-pink-500/20 rounded-full blur-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                    <div className="relative bg-garage-dark/40 backdrop-blur-md p-3 rounded-full border border-garage-cream/20 hover:border-garage-accent/50 transition-all duration-300 shadow-lg hover:shadow-xl">
                      <Instagram className="w-5 h-5 text-garage-cream group-hover:text-garage-accent transition-colors duration-300" />
                    </div>
                  </a>

                  <a
                    href="https://www.facebook.com/profile.php?id=61583633457855"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group relative"
                  >
                    <div className="absolute inset-0 bg-gradient-to-br from-blue-500/20 to-blue-600/20 rounded-full blur-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                    <div className="relative bg-garage-dark/40 backdrop-blur-md p-3 rounded-full border border-garage-cream/20 hover:border-garage-accent/50 transition-all duration-300 shadow-lg hover:shadow-xl">
                      <Facebook className="w-5 h-5 text-garage-cream group-hover:text-garage-accent transition-colors duration-300" />
                    </div>
                  </a>

                  <a
                    href="https://wa.me/+8562093124728"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group relative"
                  >
                    <div className="absolute inset-0 bg-gradient-to-br from-green-500/20 to-green-600/20 rounded-full blur-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                    <div className="relative bg-garage-dark/40 backdrop-blur-md p-3 rounded-full border border-garage-cream/20 hover:border-garage-accent/50 transition-all duration-300 shadow-lg hover:shadow-xl">
                      <MessageCircle className="w-5 h-5 text-garage-cream group-hover:text-garage-accent transition-colors duration-300" />
                    </div>
                  </a>

                  <a
                    href="https://maps.app.goo.gl/AcV6Wq24J44b4GxT6"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group relative"
                  >
                    <div className="absolute inset-0 bg-gradient-to-br from-red-500/20 to-yellow-500/20 rounded-full blur-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                    <div className="relative bg-garage-dark/40 backdrop-blur-md p-3 rounded-full border border-garage-cream/20 hover:border-garage-accent/50 transition-all duration-300 shadow-lg hover:shadow-xl">
                      <MapPin className="w-5 h-5 text-garage-cream group-hover:text-garage-accent transition-colors duration-300" />
                    </div>
                  </a>
                </div>
              </div>
            </div>
          </div>
        )}
      </header>

      {/* Main Content. Suspense boundary for lazy-loaded page chunks — the
          header (above) and footer (below) stay mounted while a page loads. */}
      <main className={hasFloatingHero ? '' : 'pt-12 md:pt-14'}>
        <Suspense fallback={<div className="min-h-screen bg-garage-dark" />}>
          {children}
        </Suspense>
      </main>

      {/* Footer with Glassmorphism */}
      <footer
        className={`relative overflow-hidden ${
          shouldHideFooterContent ? 'py-2' : 'py-6'
        }`}
      >
        {/* Background with garage theme */}
        <div className="absolute inset-0 bg-gradient-to-br from-garage-dark via-garage-metal to-garage-surface" />

        {/* Texture overlays */}
        <div className="absolute inset-0 bg-garage-workshop-texture opacity-10" />
        <div className="absolute inset-0 bg-garage-mud-texture opacity-5" />

        {/* Decorative elements */}
        <div className="absolute top-0 left-1/4 w-32 h-32 bg-garage-accent/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-40 h-40 bg-garage-primary/10 rounded-full blur-3xl" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          {!shouldHideFooterContent ? (
            // Brand description block removed per request; keep Legal access.
            <div className="flex justify-center">
              <button
                onClick={() => setShowLegalModal(true)}
                className="flex items-center gap-2 px-4 py-2 bg-garage-card/40 backdrop-blur-sm rounded-lg border border-garage-border/30 text-garage-cream/80 hover:bg-garage-accent hover:text-garage-cream transition-all duration-300 font-medium"
              >
                <Scale className="h-4 w-4" />
                {t('footer.legal')}
              </button>
            </div>
          ) : null}

          {/* Bottom section with glassmorphism */}
          <div className="mt-2 pt-2">
            {/* Social Media Links - Always Visible */}
            <div className="flex justify-center gap-2 mb-2">
              <a
                href="https://www.instagram.com/terra_lao_adventure/"
                target="_blank"
                rel="noopener noreferrer"
                className="group relative"
              >
                <div className="relative bg-garage-dark/40 backdrop-blur-md p-2 rounded-full border border-garage-cream/20 hover:border-garage-accent/50 transition-all duration-300">
                  <Instagram className="w-4 h-4 text-garage-cream group-hover:text-garage-accent transition-colors duration-300" />
                </div>
              </a>

              <a
                href="https://www.facebook.com/profile.php?id=61583633457855"
                target="_blank"
                rel="noopener noreferrer"
                className="group relative"
              >
                <div className="relative bg-garage-dark/40 backdrop-blur-md p-2 rounded-full border border-garage-cream/20 hover:border-garage-accent/50 transition-all duration-300">
                  <Facebook className="w-4 h-4 text-garage-cream group-hover:text-garage-accent transition-colors duration-300" />
                </div>
              </a>

              <a
                href="https://wa.me/+8562093124728"
                target="_blank"
                rel="noopener noreferrer"
                className="group relative"
              >
                <div className="relative bg-garage-dark/40 backdrop-blur-md p-2 rounded-full border border-garage-cream/20 hover:border-garage-accent/50 transition-all duration-300">
                  <MessageCircle className="w-4 h-4 text-garage-cream group-hover:text-garage-accent transition-colors duration-300" />
                </div>
              </a>

              <a
                href="https://maps.app.goo.gl/AcV6Wq24J44b4GxT6"
                target="_blank"
                rel="noopener noreferrer"
                className="group relative"
              >
                <div className="relative bg-garage-dark/40 backdrop-blur-md p-2 rounded-full border border-garage-cream/20 hover:border-garage-accent/50 transition-all duration-300">
                  <MapPin className="w-4 h-4 text-garage-cream group-hover:text-garage-accent transition-colors duration-300" />
                </div>
              </a>
            </div>

            <div className="bg-garage-card/20 backdrop-blur-xl rounded-lg p-2 border border-garage-accent/20">
              <div className="text-center">
                <p className="text-garage-cream text-xs font-semibold">
                  {t('footer.builtIn')}
                </p>
                <p className="text-garage-cream/80 text-xs font-medium">
                  {t('footer.by')}{' '}
                  <a
                    href="https://sabaidev.la"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-garage-accent hover:text-garage-primary transition-colors font-bold"
                  >
                    @SabaïDev
                  </a>
                </p>
              </div>
            </div>
          </div>
        </div>
      </footer>

      {/* Legal Modal with Glassmorphism - Hidden on Home and Guides pages */}
      {showLegalModal && !shouldHideFooterContent && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-garage-card/95 backdrop-blur-2xl rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden border border-garage-accent/20">
            <div className="flex items-center justify-between p-6 border-b border-garage-accent/20">
              <h2 className="text-xl font-semibold text-garage-dark font-garage-title">
                {tl('title')}
              </h2>
              <button
                onClick={() => setShowLegalModal(false)}
                className="p-2 rounded-lg bg-garage-card/50 hover:bg-garage-accent/20 text-garage-accent hover:text-garage-primary transition-all duration-300"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
              <div className="space-y-6">
                {i18n.language !== 'en' && (
                  <p className="border-l-2 border-garage-accent/40 pl-3 text-xs italic text-garage-text/70">
                    {tl('authoritative')}
                  </p>
                )}

                <section>
                  <h3 className="text-lg font-semibold text-garage-dark mb-3 font-garage-title">
                    {tl('terms')}
                  </h3>
                  <div className="text-sm text-garage-text space-y-3">
                    <LegalList
                      title={tl('bookingReq')}
                      items={tl('bookingReqItems', { returnObjects: true })}
                    />
                    <LegalList
                      title={tl('payment')}
                      items={tl('paymentItems', { returnObjects: true })}
                    />
                    <LegalList
                      title={tl('modifications')}
                      items={tl('modificationsItems', { returnObjects: true })}
                    />
                  </div>
                </section>

                <section>
                  <h3 className="text-lg font-semibold text-garage-dark mb-3 font-garage-title">
                    {tl('risk')}
                  </h3>
                  <div className="text-sm text-garage-text space-y-3">
                    <div>
                      <h4 className="font-semibold mb-2 text-red-600 dark:text-red-400">
                        {tl('warning')}
                      </h4>
                      <p className="font-medium mb-2">{tl('warningIntro')}</p>
                      <ul className="list-disc pl-6 space-y-1">
                        {(
                          tl('warningItems', {
                            returnObjects: true,
                          }) as string[]
                        ).map((it, i) => (
                          <li key={i}>{it}</li>
                        ))}
                      </ul>
                    </div>

                    <LegalList
                      title={tl('responsibilities')}
                      items={tl('responsibilitiesItems', {
                        returnObjects: true,
                      })}
                    />

                    <div className="bg-red-50 dark:bg-red-900/20 p-4 rounded-lg">
                      <h4 className="font-semibold mb-2 text-red-800 dark:text-red-300">
                        {tl('liability')}
                      </h4>
                      <p className="font-medium text-red-700 dark:text-red-300">
                        {tl('liabilityText')}
                      </p>
                    </div>
                  </div>
                </section>

                <section>
                  <h3 className="text-lg font-semibold text-garage-dark mb-3 font-garage-title">
                    {tl('insurance')}
                  </h3>
                  <div className="text-sm text-garage-text space-y-3">
                    <LegalList
                      title={tl('requiredInsurance')}
                      items={tl('requiredInsuranceItems', {
                        returnObjects: true,
                      })}
                    />
                    <LegalList
                      title={tl('coverage')}
                      items={tl('coverageItems', { returnObjects: true })}
                    />
                    <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
                      <h4 className="font-semibold mb-2 text-blue-800 dark:text-blue-300">
                        {tl('equipmentDamage')}
                      </h4>
                      <p className="text-blue-700 dark:text-blue-300">
                        {tl('equipmentDamageText')}
                      </p>
                    </div>
                  </div>
                </section>

                <section>
                  <h3 className="text-lg font-semibold text-garage-dark mb-3 font-garage-title">
                    {tl('privacy')}
                  </h3>
                  <div className="text-sm text-garage-text space-y-2">
                    <p>{tl('privacyIntro')}</p>
                    <ul className="list-disc pl-6 space-y-1">
                      {(
                        tl('privacyItems', { returnObjects: true }) as string[]
                      ).map((it, i) => (
                        <li key={i}>{it}</li>
                      ))}
                    </ul>
                    <p className="mt-3">{tl('privacyOutro')}</p>
                  </div>
                </section>

                <section>
                  <h3 className="text-lg font-semibold text-garage-dark mb-3 font-garage-title">
                    {tl('company')}
                  </h3>
                  <div className="text-sm text-garage-text space-y-2">
                    <p>
                      <strong>{tl('businessName')}</strong> Terra Lao Adventure
                    </p>
                    <p>
                      <strong>{tl('location')}</strong> {tl('locationVal')}
                    </p>
                    <p>
                      <strong>{tl('registration')}</strong>{' '}
                      {tl('registrationVal')}
                    </p>
                    <p>
                      <strong>{tl('contact')}</strong> WhatsApp +856 20 93 124
                      728
                    </p>
                  </div>
                </section>

                <section>
                  <h3 className="text-lg font-semibold text-garage-dark mb-3 font-garage-title">
                    {tl('governingLaw')}
                  </h3>
                  <div className="text-sm text-garage-text">
                    <p>{tl('governingLawText')}</p>
                  </div>
                </section>
              </div>
            </div>
            <div className="flex justify-end p-6 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={() => setShowLegalModal(false)}
                className="btn-primary"
              >
                {tl('close')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Chatbot Widget - Floating on all pages (can be disabled in admin) */}
      {isChatbotEnabled && (
        <Suspense fallback={null}>
          <ChatbotWidget />
        </Suspense>
      )}

      {/* Cart Drawer - Slides in from right */}
      <CartDrawer />
    </div>
  )
}

export default Layout
